// The ledger export (QA-291; the accounting prototype's Imports & exports): once QuickBooks Online
// or Xero is connected, match our accounts to theirs (by name, from their own chart, or by hand),
// run an export of a month in detail or as daily summaries, and see how the last ones went. An
// export can't run while an account with postings has no match; each exported entry keeps the
// target's id, so a re-run sends only what is new. The worker sends; this screen asks and waits.

import { useEffect, useRef, useState } from 'react';
import { dayIn, dayText, monthName, monthOf, previousMonth, todayIn } from './model.ts';
import { Loading, Panel, useAct, useQuery } from './ui.tsx';
import './setup.css';
import { scrollFocus } from '../shared/scrollFocus.ts';

type Target = 'quickbooks' | 'xero';
const LABEL: Record<Target, string> = { quickbooks: 'QuickBooks Online', xero: 'Xero' };

interface Overview {
  accounts: { accountId: string; code: string; name: string; externalAccount: string | null }[];
  unmapped: number;
  canEdit: boolean;
  /** Their chart from the last Match by name, or null until then. */
  theirChart: { externalId: string; code: string | null; name: string }[] | null;
  lastMonth: { from: string; to: string; closed: boolean };
  runs: {
    exportId: string;
    target: Target;
    periodStart: string;
    periodEnd: string;
    mode: 'summary' | 'detail';
    status: 'pending' | 'done' | 'failed';
    sentCount: number;
    skippedCount: number;
    error: string | null;
    runAt: string;
  }[];
}

const MODE: Record<'summary' | 'detail', string> = {
  detail: 'Every journal entry',
  summary: 'Daily summaries per account',
};
const STATUS = {
  pending: ['Sending…', 'brand'],
  done: ['Done', 'ok'],
  failed: ['Failed', 'bad'],
} as const;

/** The last four months, this one to date. */
function months(today: string): { value: string; label: string; from: string; to: string }[] {
  const out = [];
  let [from, to] = monthOf(today);
  for (let i = 0; i < 4; i++) {
    out.push({
      value: from,
      label: i === 0 ? `${monthName(from)} (to date)` : monthName(from),
      from,
      to: i === 0 ? today : to,
    });
    [from, to] = previousMonth(from);
  }
  return out;
}

export function LedgerExport({
  target,
  toast,
  timeZone,
}: {
  target: Target;
  toast: (m: string) => void;
  timeZone?: string;
}) {
  const q = useQuery<Overview>('exports.overview', { target });
  const act = useAct(toast, () => q.reload());
  const today = todayIn(timeZone);
  const choices = months(today);
  // Unset: last month once the books have closed it, else this month to date.
  const [picked, setPicked] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<'summary' | 'detail'>('detail');
  const [hand, setHand] = useState<Record<string, string>>({});
  const [waiting, setWaiting] = useState<'match' | 'export' | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  // While the worker matches or sends, read again every second and a half, for up to 20 seconds.
  useEffect(() => {
    if (!waiting) return;
    const started = Date.now();
    timer.current = setInterval(() => {
      void q.reload();
      if (Date.now() - started > 20_000) setWaiting(null);
    }, 1500);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [waiting]);
  const o = q.data;
  const latest = o?.runs.find((r) => r.target === target);
  useEffect(() => {
    if (waiting === 'export' && latest && latest.status !== 'pending') {
      setWaiting(null);
      toast(
        latest.status === 'done'
          ? `Export finished · ${latest.sentCount} ${latest.sentCount === 1 ? 'entry' : 'entries'} sent`
          : `The export failed: ${latest.error ?? 'try again'}`,
      );
    }
    if (waiting === 'match' && o && o.unmapped === 0) {
      setWaiting(null);
      toast('All accounts matched.');
    }
  }, [o, latest, waiting]);

  if (!o) return <Loading error={q.error} what="the export" kind="table" retry={() => void q.reload()} />;
  const canEdit = o.canEdit;
  const unmatched = o.accounts.filter((a) => !a.externalAccount);
  const matched = o.accounts.filter((a) => a.externalAccount);
  const period = picked ?? (o.lastMonth.closed ? choices[1]!.value : choices[0]!.value);
  const chosen = choices.find((c) => c.value === period)!;
  const chart = o.theirChart;
  const theirLabel = (x: { code: string | null; name: string; externalId: string }) =>
    `${x.code ? `${x.code} ` : ''}${x.name}`;
  const needle = search.trim().toLowerCase();
  /** Their accounts for one of ours: the search narrows them; the one chosen always stays. */
  const options = (chosenId: string) =>
    (chart ?? []).filter(
      (x) => !needle || x.externalId === chosenId || theirLabel(x).toLowerCase().includes(needle),
    );
  const pick = (a: Overview['accounts'][number]) => (
    <td>
      {chart ? (
        <select
          className="field"
          aria-label={`${LABEL[target]} account for ${a.code} ${a.name}`}
          value={hand[a.accountId] ?? a.externalAccount ?? ''}
          onChange={(e) => setHand({ ...hand, [a.accountId]: e.target.value })}
        >
          <option value="">Choose their account…</option>
          {options(hand[a.accountId] ?? a.externalAccount ?? '').map((x) => (
            <option key={x.externalId} value={x.externalId}>
              {theirLabel(x)}
            </option>
          ))}
        </select>
      ) : (
        <input
          className="field"
          aria-label={`${LABEL[target]} account for ${a.code} ${a.name}`}
          value={hand[a.accountId] ?? a.externalAccount ?? ''}
          onChange={(e) => setHand({ ...hand, [a.accountId]: e.target.value })}
        />
      )}
    </td>
  );
  const changed = Object.entries(hand).filter(
    ([id, v]) => v.trim() && v !== o.accounts.find((a) => a.accountId === id)?.externalAccount,
  );
  const run = async () => {
    const r = await act.run('exports.runLedgerExport', { target, from: chosen.from, to: chosen.to, mode });
    if (r) setWaiting('export');
  };
  const match = async () => {
    const r = await act.run('exports.matchAccounts', { target });
    if (r) setWaiting('match');
  };
  const saveHand = async () => {
    const mappings = changed.map(([accountId, v]) => ({ accountId, externalAccount: v.trim() }));
    if (!mappings.length) return toast(chart ? 'Choose their account first.' : 'Type their account first.');
    if (await act.run('exports.mapAccounts', { target, mappings }, 'Accounts mapped.')) setHand({});
  };
  return (
    <Panel
      title={`Export to ${LABEL[target]}`}
      aside={
        unmatched.length ? (
          <span className="chip say bad">
            {unmatched.length} {unmatched.length === 1 ? 'account' : 'accounts'} without a match
          </span>
        ) : (
          <span className="chip say ok">All accounts matched</span>
        )
      }
    >
      <p className="muted">
        Each exported entry keeps its {LABEL[target]} id, so a re-run sends only what is new.
      </p>
      {unmatched.length > 0 && (
        <div className="export-map">
          <p>
            <b>
              {unmatched.length} {unmatched.length === 1 ? 'account has' : 'accounts have'} no {LABEL[target]}{' '}
              match yet.
            </b>{' '}
            The export waits until every account with postings has one.
          </p>
          {canEdit && (
            <>
              <div className="row-actions">
                <button
                  className="btn"
                  type="button"
                  disabled={act.busy || waiting === 'match'}
                  onClick={() => void match()}
                >
                  {waiting === 'match' ? 'Matching…' : 'Match by name'}
                </button>
              </div>
              {chart && chart.length > 12 && (
                <label className="search">
                  Search their chart
                  <input
                    className="field"
                    type="search"
                    value={search}
                    placeholder="A code or a name"
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </label>
              )}
              {!chart && (
                <p className="muted small">
                  Match by name also reads {LABEL[target]}’s chart, so you can pick their account here.
                </p>
              )}
              <div className="table-wrap" ref={scrollFocus}>
                <table className="grid-table" aria-label="Accounts without a match">
                  <thead>
                    <tr>
                      <th>Our account</th>
                      <th>{target === 'xero' ? 'Xero account code' : 'QuickBooks account id'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatched.map((a) => (
                      <tr key={a.accountId}>
                        <td>
                          {a.code} {a.name}
                        </td>
                        {pick(a)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="row-actions">
                <button className="btn" type="button" disabled={act.busy} onClick={() => void saveHand()}>
                  Map accounts
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {matched.length > 0 && (
        <details className="more">
          <summary>{matched.length} accounts matched</summary>
          {canEdit && chart ? (
            <>
              <div className="table-wrap" ref={scrollFocus}>
                <table className="grid-table" aria-label="Accounts matched">
                  <thead>
                    <tr>
                      <th>Our account</th>
                      <th>Their account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matched.map((a) => (
                      <tr key={a.accountId}>
                        <td>
                          {a.code} {a.name}
                        </td>
                        {pick(a)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {unmatched.length === 0 && (
                <div className="row-actions">
                  <button
                    className="btn"
                    type="button"
                    disabled={act.busy || !changed.length}
                    onClick={() => void saveHand()}
                  >
                    Map accounts
                  </button>
                </div>
              )}
            </>
          ) : (
            <ul className="matched">
              {matched.map((a) => {
                const theirs = chart?.find((x) => x.externalId === a.externalAccount);
                return (
                  <li key={a.accountId}>
                    {a.code} {a.name} → {theirs ? theirLabel(theirs) : a.externalAccount}
                  </li>
                );
              })}
            </ul>
          )}
        </details>
      )}
      {canEdit && (
        <div className="fgrid">
          <label>
            Period
            <select className="field" value={period} onChange={(e) => setPicked(e.target.value)}>
              {choices.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            {picked === null && (
              <span className="muted small">
                {o.lastMonth.closed
                  ? `${monthName(o.lastMonth.from)} is closed.`
                  : `${monthName(o.lastMonth.from)} isn’t closed yet, so this month so far.`}
              </span>
            )}
          </label>
          <label>
            Detail
            <select
              className="field"
              value={mode}
              onChange={(e) => setMode(e.target.value as 'summary' | 'detail')}
            >
              <option value="detail">{MODE.detail}</option>
              <option value="summary">{MODE.summary}</option>
            </select>
          </label>
          <div className="row-actions">
            <button
              className="btn primary"
              type="button"
              disabled={act.busy || waiting === 'export' || unmatched.length > 0}
              onClick={() => void run()}
            >
              {waiting === 'export' ? 'Exporting…' : 'Run export'}
            </button>
          </div>
          {mode === 'summary' && <p className="muted small">Daily summaries cover closed months only.</p>}
        </div>
      )}
      {o.runs.length ? (
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table" aria-label="Last exports">
            <thead>
              <tr>
                <th>When</th>
                <th>To</th>
                <th>Period</th>
                <th>Detail</th>
                <th>Sent</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {o.runs.map((r) => (
                <tr key={r.exportId}>
                  <td>{dayText(dayIn(r.runAt, timeZone))}</td>
                  <td>{LABEL[r.target]}</td>
                  <td>
                    {dayText(r.periodStart)} – {dayText(r.periodEnd)}
                  </td>
                  <td>{MODE[r.mode]}</td>
                  <td>
                    {r.sentCount}
                    {r.skippedCount ? (
                      <span className="muted small"> · {r.skippedCount} already there</span>
                    ) : null}
                  </td>
                  <td>
                    <span className={`chip say ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span>
                    {r.error && <span className="bad-text small"> {r.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">No exports yet.</p>
      )}
    </Panel>
  );
}
